// Help.js
import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaDiscord } from 'react-icons/fa';
import './Help.css';

function Help() {
  const navigate = useNavigate();
  const location = useLocation();
  const touchdesignerRef = useRef(null);

  // When this page loads, check if we should scroll to TouchDesigner info.
  useEffect(() => {
    if (
      location.state &&
      location.state.scrollTo === 'touchdesigner' &&
      touchdesignerRef.current
    ) {
      touchdesignerRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location]);

  const handleBack = () => {
    navigate('/');
  };

  return (
    <div className="help-container">
      <header className="help-header">
        <h1>Help & Support</h1>
        <button className="back-button" onClick={handleBack}>
          ← Back to Home
        </button>
      </header>
      <main className="help-content">
        <h2>
          Get Help
        </h2>
          <p>
            For questions, bugs, or feature requests, please join the Discord community. The Discord community will have the most up-date information, including video tutorials, updates and more.
          </p>
          <div className="mobile-download">

            <div className="download-buttons">
              <a
                className="download-button"
                href="https://discord.gg/2sF5VJxRy8"
                aria-label="Join the Discord"
              >
                <FaDiscord className="download-icon" /> Join the Discord
              </a>
            </div>
          </div>
          <br />
        {/* Getting Started Section */}
        <section id="getting-started">
          <h2>Getting Started</h2>
          <p>
            Welcome to SeeOSC! Follow the steps below to set up and start using the app.
          </p>

          {/* Subsection: Sending Sensor Data via OSC */}
          <div className="getting-started-subsection">
            <h3>1. Sending Sensor Data via OSC</h3>
            <p>
              SeeOSC allows you to send various sensor data from your mobile device to your computer using the{' '}
              <strong>Open Sound Control (OSC)</strong> protocol. This feature requires no additional software or setup beyond your OSC receiving application.
            </p>
            <ol>
              <li id="help-list-item">
                <strong>Download SeeOSC App</strong> (currently only available on iOS): Visit the{' '}
                <a
                  href="https://apps.apple.com/au/app/seeosc/id6740987202"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Apple App Store
                </a>{' '}
                to download and install the SeeOSC Mobile App on your iOS device.
              </li>
              <li id="help-list-item">
                <strong>Connect to the Same Network:</strong> Ensure that both your mobile device and computer are connected to the same local network (Wi-Fi).
              </li>
              <li id="help-list-item">
                <strong>Launch and Configure:</strong> Open the SeeOSC Mobile App and configure desired sensor and network settings.
              </li>
              <li id="help-list-item">
                <strong>Network Settings:</strong> On the Network Settings tab, enter the computer's IP address and adjust ports to match your OSC-compatible software (e.g., TouchDesigner, Max/MSP, Processing).
              </li>
              <li id="help-list-item">
                <strong>Start Using:</strong> Launch your OSC-compatible software on your computer to receive and utilize the sensor data in real time.
              </li>
            </ol>
          </div>

          {/* Subsection: Sending and Receiving Video Streams */}
          <div className="getting-started-subsection">
            <h3>2. Sending and Receiving Video Streams</h3>
            <p>
              SeeOSC offers flexible video streaming. You can send your device's camera feed and/or receive a video stream back to display on your device. Choose the method that best suits your setup:
            </p>
            <div className="video-stream-options">

              {/* NDI® Streaming Option - NEW */}
              <div className="ndi-integration">
                <h4>NDI® Streaming (Recommended for Local Networks)</h4>
                <p>
                  Utilize the industry-standard <strong>NDI®</strong> (Network Device Interface) protocol for high-quality, low-latency video and audio transmission over your local network. This is ideal for professional workflows and integration with NDI-compatible software. <a href="https://ndi.video/" target="_blank" rel="noopener noreferrer">Learn more about NDI</a>.
                </p>
                <ol>
                  <li id="help-list-item">
                    <strong>Connect to Same Network:</strong> Ensure your mobile device and receiving computer are on the same Wi-Fi or wired network.
                  </li>
                  <li id="help-list-item">
                    <strong>Select NDI in SeeOSC:</strong> In the Media tab, choose "NDI" as your video streaming protocol and select required settings (send/receive audio/video and camera).
                  </li>
                  <li id="help-list-item">
                    <strong>Receiving Software:</strong> Launch your NDI-compatible software on your computer (e.g., TouchDesigner with NDI In TOP, OBS Studio with NDI plugin, vMix, NDI Studio Monitor from the free <a href="https://ndi.video/tools/" target="_blank" rel="noopener noreferrer">NDI Tools</a>).
                  </li>
                  <li id="help-list-item">
                    <strong>Select NDI Specifc Settings:</strong> Select 'Video Only' or 'Video & Sensors' from the Start tab. If needed, update the 'Sender Name', 'Machine Name', resolution and frame rate.
                  </li>
                  <li id="help-list-item">
                    <strong>Start Streaming:</strong> Review your configuration, refresh and select an available NDI source (if receiving video and/or audio) and press 'Start NDI Session'.
                  </li>
                </ol>
                <p>
                  <em>NDI requires compatible receiving software.</em>
                </p>
              </div>

              {/* WebRTC/TouchDesigner Integration Instructions */}
              <div className="touchdesigner-integration" ref={touchdesignerRef}>
                {/* Renamed title slightly for clarity */}
                <h4>WebRTC/TouchDesigner Integration (Alternative)</h4>
                <p>
                  This method uses WebRTC for direct peer-to-peer connection, often suitable for different network setups or when NDI is not available. The provided TouchDesigner component simplifies this setup.
                </p>
                <ol>
                  <li id="help-list-item">
                    <strong>Select WebRTC in SeeOSC:</strong> In the SeeOSC mobile app's settings, ensure "WebRTC/TouchDesigner" (or similar) is selected as the video streaming protocol.
                  </li>
                  <li id="help-list-item">
                    <strong>Download the TOX File:</strong>{' '}
                    <a
                      href="/downloads/SeeOSC_TouchIntegration.zip"
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Download
                    </a>{' '}
                    the TouchDesigner TOX file.
                  </li>
                  <li id="help-list-item">
                    <strong>Open TouchDesigner:</strong> Launch TouchDesigner on your system.
                  </li>
                  <li id="help-list-item">
                    <strong>Import the TOX File:</strong> In TouchDesigner, navigate to <em>File &gt; Import Component</em> and select the downloaded TOX file.
                  </li>
                  <li id="help-list-item">
                    <strong>Configure the Component:</strong> Inside the imported component's parameters, set the correct local IP address (not just localhost) of the machine running TouchDesigner and ensure the ports match those configured in the SeeOSC app's Network Settings.
                  </li>
                  <li id="help-list-item">
                    <strong>Connect from SeeOSC:</strong> Configure your desired settings in the SeeOSC app, then press the 'Video & Sensors' button.
                  </li>
                  <li id="help-list-item">
                    <strong>Initiate Connection in TouchDesigner:</strong> Once SeeOSC is on the streaming screen (and configured correctly), the TouchDesigner component should detect the potential connection. Press the 'Connect' or 'Call' button inside the TouchDesigner component to establish the WebRTC link.
                  </li>
                </ol>
                <p>
                  <em>This method requires the specific TouchDesigner component for ease of use but offers flexibility across different network conditions.</em>
                </p>
              </div>

              {/* Desktop Companion Instructions - Assuming this uses WebRTC/TD primarily */}
              <div className="desktop-companion">
                <h4>Desktop Companion App (WebRTC/Spout)</h4>
                <p>
                  The Desktop Companion App acts as a bridge, primarily using WebRTC to communicate with the mobile app and interfacing with Spout on Windows for sending/receiving video with other desktop applications.
                </p>
                <ol>
                  <li id="help-list-item">
                    <strong>Select WebRTC in SeeOSC:</strong> Ensure "WebRTC/TouchDesigner" is selected in the mobile app.
                  </li>
                  <li id="help-list-item">
                    <strong>Download the Companion App: </strong>
                    {/* Keep download link or update if needed */}
                    <a
                      href="/downloads/SeeOSC_win.zip"
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Download for Windows
                    </a>. (Mac version coming soon).
                  </li>
                  <li id="help-list-item">
                    <strong>Unzip & Launch:</strong> Unzip and run the SeeOSC Companion App. Ensure it's running before trying to stream.
                  </li>
                  <li id="help-list-item">
                    <strong>Configure via Web UI:</strong> Access the Companion App's settings via its web interface (usually `http://127.0.0.1:8080`). Select a Spout source if you want to send video *from* your PC *to* the mobile app. Incoming video from the mobile app will typically be available as a Spout output named "SeeOSC Input".
                  </li>
                  <li id="help-list-item">
                    <strong>Connect Mobile Device:</strong> Open the SeeOSC Mobile App. In Network Settings, enter the computer's IP address and the port used by the Companion App (check its Web UI or console output).
                  </li>
                  <li id="help-list-item">
                    <strong>Enable Video & Start:</strong> Enable "Send Video Feed" in the mobile app if needed. Tap "Video & Sensors" to begin. The connection should establish automatically with the running Companion App.
                  </li>
                </ol>
                <p>
                  <em>The Companion App simplifies bridging WebRTC with Spout on Windows but adds an extra software layer compared to direct NDI or the TouchDesigner integration.</em>
                </p>
              </div>

            </div> {/* End video-stream-options */}
          </div>
        </section>

        {/* Troubleshooting Section */}
        <section id="troubleshooting">
          <h2>Troubleshooting</h2>
          <p>Encountering issues? Here are some common problems and their solutions.</p>

          <h3>General Connection Issues (All Methods)</h3>
          <ul>
            <li id="help-list-item">
              <strong>Ensure Same Network:</strong> Verify both your mobile device and computer are on the same Wi-Fi network. Avoid guest networks or complex enterprise networks that might block communication.
            </li>
            <li id="help-list-item">
              <strong>Check IP Addresses & Ports:</strong> Double-check that the IP address entered in the SeeOSC app is the correct *local* IP of your computer, and that the ports match between the app and the receiving software/companion app.
            </li>
            <li id="help-list-item">
              <strong>Firewall Settings:</strong> Your computer's firewall (Windows Defender Firewall, macOS Firewall, or third-party) might be blocking incoming connections. You may need to create rules to allow traffic on the specific ports used by SeeOSC, NDI, or the Companion App.
            </li>
            <li id="help-list-item">
              <strong>Restart Everything:</strong> Sometimes a simple restart fixes temporary glitches. Restart the SeeOSC mobile app, the receiving software (TouchDesigner, OBS, etc.), the Companion App (if used), and even both your mobile device and computer.
            </li>
          </ul>

          <h3>NDI Specific Issues</h3>
          <ul>
            <li id="help-list-item">
              <strong>NDI Source Not Appearing:</strong>
              <ul>
                <li>Make sure your receiving software actually supports NDI input.</li>
                <li>Try using the NDI Studio Monitor (from <a href="https://ndi.video/tools/" target="_blank" rel="noopener noreferrer">NDI Tools</a>) on your computer to see if the source is visible there. This helps isolate if the issue is with SeeOSC or your primary receiving software.</li>
                <li>Some networks block mDNS (used for NDI discovery). Check network settings or try configuring an NDI Discovery Server if you have a more complex setup.</li>
              </ul>
            </li>
            <li id="help-list-item">
              <strong>Poor NDI Performance (Stuttering, Lag):</strong>
              <ul>
                <li>NDI requires a stable, high-bandwidth network. Strong Wi-Fi (5GHz, Wi-Fi 6) is best.</li>
                <li>Avoid network congestion. Too many devices or heavy traffic can impact NDI.</li>
                <li>Ensure your computer has sufficient CPU resources. While NDI receiving is often efficient, decoding multiple high-resolution streams can be demanding.</li>
              </ul>
            </li>
          </ul>

          <h3>WebRTC/TouchDesigner/Companion App Issues</h3>
          <ul>
            <li id="help-list-item">
              <strong>Companion App Not Connecting:</strong>
              <ul>
                <li>Is the Companion App actually running on the computer? Check the task manager or system tray.</li>
                <li>Did you configure the correct IP and port in the mobile app matching the Companion App?</li>
              </ul>
            </li>
            <li id="help-list-item">
              <strong>TouchDesigner Component Not Connecting:</strong>
              <ul>
                <li>Did you press the 'Connect'/'Call' button inside the TouchDesigner component *after* starting the stream on the mobile app?</li>
                <li>Is the IP address in the component parameters correct (local IP, not localhost)?</li>
                <li>Are the ports correct?</li>
              </ul>
            </li>
            <li id="help-list-item">
              <strong>Video Stream Freezes or is Black:</strong>
              <ul>
                <li>Check network stability. WebRTC can be sensitive to packet loss.</li>
                <li>Ensure the camera permissions are granted to the SeeOSC app on your mobile device.</li>
                <li>If using the Companion App, check its logs or console output for errors.</li>
              </ul>
            </li>
          </ul>

          <h3>Sensor Data Not Receiving on Computer</h3>
          <ul>
            <li id="help-list-item">
              <strong>Check OSC Settings:</strong> Ensure the IP address and *receiving* port in the SeeOSC app match what your OSC software (TouchDesigner OSC In CHOP, Max/MSP `udpreceive`, etc.) is listening on. Also check the *sending* port if your software needs to send back to SeeOSC.
            </li>
            <li id="help-list-item">
              <strong>Verify OSC Software Setup:</strong> Is your OSC software correctly configured to listen for incoming messages on the specified port?
            </li>
            <li id="help-list-item">
              <strong>Correct OSC Addresses:</strong> Ensure the OSC addresses SeeOSC sends (e.g., `/seeosc/accel/x`) match what your software expects. Check the app's documentation or settings for the exact addresses used.
            </li>
          </ul>
        </section>

        {/* Additional Resources Section */}
        <section id="additional-resources">
          <h2>Additional Resources</h2>
          <ul>
            {/* Update link/text if needed */}
            <li id="help-list-item">
              <a href="/downloads/SeeOSC_win.zip" download target="_blank" rel="noopener noreferrer">
                Download Companion App (Windows)
              </a>
            </li>
            <li id="help-list-item">
              <a href="/downloads/SeeOSC_TouchIntegration.zip" download target="_blank" rel="noopener noreferrer">
                Download TouchDesigner Integration (.tox)
              </a>
            </li>
            {/* NDI Tools Link */}
            <li id="help-list-item">
              <a href="https://ndi.video/tools/" target="_blank" rel="noopener noreferrer">
                Download Free NDI Tools (External Link)
              </a> - Useful for testing NDI streams.
            </li>
            <li id="help-list-item">
              <a href="/privacypolicy" onClick={(e) => { e.preventDefault(); navigate('/privacypolicy'); }}>
                Privacy Policy
              </a>
            </li>
            {/* Update email if different */}
            <li id="help-list-item">
              <a href="mailto:support@seeosc.com">Contact Support</a>
            </li>
          </ul>
        </section>
      </main>
      <footer className="help-footer">
        © 2025 Jayden Allen |{' '}
        <a
          href="/privacypolicy"
          onClick={(e) => {
            e.preventDefault();
            navigate('/privacypolicy');
          }}
          style={{ marginRight: '8px' }}
        >
          Privacy Policy
        </a> |
        {/* NDI Attribution */}
        <span style={{ marginLeft: '8px', color: '#888', fontSize: '0.9em' }}>
          NDI® is a registered trademark of Vizrt NDI AB.
        </span>
      </footer>
    </div>
  );
}

export default Help;